export interface Project {
  id: string
  name: string
  apiUri: string
}

export interface Service {
  id: string
  code: string
  name: string
}

export interface UserInfo {
  sub: string
  emailAddress: string
  firstName?: string
  lastName?: string
  profilePicture?: string
}

export interface User {
  username: string
  emailAddress: string
  firstName?: string
  lastName?: string
  profilePicture?: string
  groups: string[]
  projects: Project[]
  lastSignIn?: string
  enabled: boolean
  status: UserStatus
}

/**
 * We obtain the 'me' groups via the cognito token (authGroups) instead of
 * using User.goups. This helps the UI stay in sync with the capabilities
 * of the current auth token.
 */
export type UserWithoutGroups = Omit<User, 'groups'>

export enum UserStatus {
  Unconfirmed = 'UNCONFIRMED',
  Confirmed = 'CONFIRMED',
  Archived = 'ARCHIVED',
  Compromised = 'COMPROMISED',
  Unknown = 'UNKNOWN',
  ResetRequired = 'RESET_REQUIRED',
  ForceChangePassword = 'FORCE_CHANGE_PASSWORD',
}

export interface TestRegistrationKey {
  id: string
  owner: string
  publicKey: string
}

export const identityDeviceNotFound = 'sudoplatform.identity.DeviceNotFound'
export const virtualCardsSimExcessiveReversal =
  'sudoplatform.virtual-cards.ExcessiveReversalError'
export const virtualCardsSimExcessiveRefund =
  'sudoplatform.virtual-cards.ExcessiveRefundError'
export const virtualCardsCardNotFoundError =
  'sudoplatform.virtual-cards.CardNotFoundError'
export const identityUserNotFound = 'sudoplatform.identity.UserNotFound'

export interface WhitelistDeviceInput {
  deviceId: string
  type: 'IOS' | 'ANDROID'
}

export interface WhitelistDeviceStatus {
  deviceId: string
  expiry: string
}

export interface RetrieveDeviceCheckInput {
  deviceId: string
  type: 'IOS'
}

export interface RetrieveDeviceRegistrationStatusInput {
  deviceId: string
  type: 'ANDROID' // only use case
}

export interface DeviceCheckInput {
  token: string
  buildType: 'debug' | 'release'
}

export interface DeviceCheckStatus {
  bit0: boolean
  bit1: boolean
  lastUpdatedTime: string
}

export interface DeviceRegistrationStatus {
  deviceId: string
  type: string
  hasReachedRegistrationLimit: boolean
  lastUpdatedAt: string
}

export interface WhitelistDeviceStatus {
  deviceId: string
  expiry: string
}

export interface SigningCertificateFingerprint {
  fingerprint: string
  friendlyName: string
}

export interface SigningCertificateFingerprints {
  items: SigningCertificateFingerprint[]
}

export interface MutateSigningCertificateFingerprintStatus {
  id: string
}

export interface UploadSigningCertificateFingerprintInput {
  fingerprint: string
  friendlyName: string
}

export interface SafetyNetPackageNameInput {
  packageName: string
}

export interface SafetyNetPackageName {
  packageName: string
}

export interface ListSafetyNetPackageNamesResult {
  items: SafetyNetPackageName[]
  nextToken: string
}

export interface IdentityServiceApi {
  IssueTestUserKeyResult: {
    keyId: string
    privateKeyData: string
  }

  ListTestRegistrationKeysResult: {
    nextToken: string
    items: TestRegistrationKey[]
  }

  ListTestUserKeysInput: {
    limit: number
    paginationToken: string
  }

  DeviceCheckKeyInput: {
    issuer: string
    keyId: string
    privateKey: string
  }

  DeviceCheckKeyMetaData: {
    issuer: string
    keyId: string
  }

  RetrieveDeviceCheckKeyMetaDataInput: {
    buildType: 'debug' | 'release'
  }
}

export interface ChartData {
  id: string
  columnDefinitions: ChartColumnDefinition[]
  dataSets: ChartDataSet[]
}

export interface ChartColumnDefinition {
  id: string
  type?: 'string' | 'date' | 'number'
}

export interface ChartDataSet {
  id: string
  rows: ChartRow[]
}

export type ChartRow = Record<string, ChartDataValue>

export type ChartDataValue = number | string | boolean
